import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { useApercu } from '../ndf/ui/useApercu';
import { useNdfMoi } from '../../hooks/useNdfMoi';
import {
  buildModules,
  buildSections,
  navigationParSections,
  type ModuleDef,
  type SectionDef,
} from './modules';
import { aValiderDansSection, useCompteurs, type Compteurs } from '../../hooks/useCompteurs';

/**
 * Layout principal : sidebar gauche (modules) + header contextuel (sous-menus
 * du module actif) + zone de contenu. Responsive : sur mobile la sidebar
 * devient un panneau coulissant (hamburger).
 *
 * SUR L'ACCUEIL, la sidebar disparaît : cet écran EST la navigation, les cartes
 * y suffisent. La garder afficherait deux fois le même choix, dont l'une en
 * petit sur le côté.
 */

/** L'accueil : le seul écran qui se passe du menu latéral. */
const ACCUEIL = '/accueil';

export function Layout() {
  const { user, logout } = useUser();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // En aperçu de rôle, la navigation doit elle aussi refléter ce que la
  // personne observée voit. Sans cela, on lui prête un menu complet — factures,
  // paiements, administration — auquel elle n'a en réalité aucun accès, et
  // l'aperçu ment sur le point le plus visible de l'écran.
  const enApercu = Boolean(useApercu());
  const ndf = useNdfMoi();
  const isAdmin = user?.role === 'admin' && !enApercu;
  const isValidator = !!user?.is_payment_validator && !enApercu;
  // `ndf` décrit déjà la personne ENDOSSÉE (l'en-tête d'aperçu voyage avec la
  // requête) ; `isAdmin`/`isValidator`, eux, sont neutralisés plus haut. Le menu
  // est donc exactement celui de la personne observée.
  const modules: ModuleDef[] = buildModules(isAdmin, isValidator, ndf);
  // Les sections ne servent qu'aux profils qui portent plusieurs casquettes.
  // En aperçu de rôle, on suit ce que voit la personne observée.
  const sections = navigationParSections(isAdmin, ndf)
    ? buildSections(isAdmin, isValidator, ndf)
    : null;
  const { data: compteurs } = useCompteurs();

  // L'accueil se passe de toute la navigation habituelle : ses cartes SONT le
  // menu. La page porte son propre en-tête (identité + déconnexion) — sans quoi
  // on ne pourrait plus se déconnecter sans quitter l'accueil d'abord.
  const surAccueil = location.pathname === ACCUEIL;

  // Module actif : celui dont une SOUS-ENTRÉE correspond à l'adresse courante.
  //
  // Le premier segment de l'URL ne suffit plus : depuis le regroupement en
  // sections, `/notes-de-frais/parametrage` appartient au module « Paramètres »
  // et `/ressources-humaines/salaries` au module « Salariés ». S'en tenir au
  // segment aurait allumé la mauvaise entrée et laissé la bonne section repliée
  // — on arrive alors sur un écran dont l'entrée est cachée dans un tiroir
  // fermé. On garde le segment en repli, pour les écrans hors modules.
  //
  // CONTEXTE D'ORIGINE. Un écran de détail vit sous l'adresse de sa ressource
  // (`/conges/demandes/:id`), qui commence par celle d'un onglet — « Mes
  // demandes » s'allumait donc même quand la fiche était ouverte depuis le
  // registre RH ou depuis un calendrier d'équipe. On avait le sentiment d'être
  // téléporté ailleurs, ce qui est exactement ce qu'un détail ne doit pas faire.
  //
  // L'écran d'où l'on vient est donc porté par l'adresse, en `?depuis=`, et
  // c'est LUI qui décide de l'onglet actif. Aucun écran n'a besoin de connaître
  // cette mécanique : il suffit d'ouvrir le détail avec le paramètre.
  const depuis = new URLSearchParams(location.search).get('depuis');
  const chemin = depuis && depuis.startsWith('/') ? depuis : location.pathname;
  const activeModule =
    modules.find((m) => m.sub.some((sub) => sub.show && (chemin === sub.to || chemin.startsWith(sub.to + '/')))) ??
    modules.find((m) => m.key === (chemin.split('/')[1] ?? ''));
  const activeKey = activeModule?.key ?? chemin.split('/')[1] ?? '';
  // Les écrans d'administration des accès ne sont plus un cas particulier : ce
  // sont les sous-onglets du module « Utilisateurs ». Le cas particulier qui
  // vivait ici faisait remplacer les onglets du module actif par ceux de
  // l'administration — l'incohérence relevée en recette.
  const subItems = activeModule?.sub.filter((s) => s.show) ?? [];

  const isSubActive = (to: string) => chemin === to || chemin.startsWith(to + '/');

  const sidebar = (
    <SidebarContent
      modules={modules}
      sections={sections}
      compteurs={compteurs}
      activeKey={activeKey}
      isAdmin={isAdmin}
      user={user}
      onLogout={logout}
      onNavigate={() => setMobileOpen(false)}
    />
  );

  return (
    <div className="min-h-screen flex bg-slate-100">
      {/* Sidebar desktop (dans le flux) : masquée sur l'accueil */}
      {!surAccueil && (
        <aside className="hidden md:flex md:flex-col md:w-52 md:shrink-0 border-r border-slate-200 bg-slate-50">
          {sidebar}
        </aside>
      )}

      {/* Sidebar mobile (panneau coulissant) */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 bg-slate-900/40 z-30 md:hidden"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="fixed inset-y-0 left-0 w-64 z-40 flex flex-col border-r border-slate-200 bg-slate-50 md:hidden">
            {sidebar}
          </aside>
        </>
      )}

      {/* Colonne contenu */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Barre mobile avec hamburger : masquée sur l'accueil */}
        <div
          className={`${surAccueil ? 'hidden' : 'md:hidden flex'} items-center gap-3 border-b border-slate-200 bg-white px-4 h-12`}
        >
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="text-slate-600 hover:text-slate-900 text-xl leading-none"
            aria-label="Ouvrir le menu"
          >
            ☰
          </button>
          <span className="font-semibold tracking-tight">GERTRUDE</span>
        </div>

        {/* Header contextuel (sous-menus du module actif) */}
        {!surAccueil && subItems.length > 1 && (
          <header className="bg-white border-b border-slate-200 shadow-sm">
            <nav className="flex items-center gap-1 px-4 md:px-6 h-12 overflow-x-auto">
              {subItems.map((s) => (
                <NavLink
                  key={s.to}
                  to={s.to}
                  className={() =>
                    `px-3 py-1.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${
                      isSubActive(s.to)
                        ? 'border-blue-600 text-blue-700 font-medium'
                        : 'border-transparent text-slate-600 hover:text-slate-900'
                    }`
                  }
                >
                  {s.label}
                </NavLink>
              ))}
            </nav>
          </header>
        )}

        <main
          className={`flex-1 px-4 md:px-8 py-6 md:py-8 max-w-7xl w-full ${surAccueil ? 'mx-auto' : ''}`}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function SidebarContent({
  modules,
  sections,
  compteurs,
  activeKey,
  isAdmin,
  user,
  onLogout,
  onNavigate,
}: {
  modules: ModuleDef[];
  sections: SectionDef[] | null;
  compteurs: Compteurs | undefined;
  activeKey: string;
  isAdmin: boolean;
  user: ReturnType<typeof useUser>['user'];
  onLogout: () => void;
  onNavigate: () => void;
}) {
  return (
    <div className="flex flex-col h-full">
      {/* Logo + nom : et retour à l'accueil. C'est le geste attendu partout
          ailleurs sur le web ; sans lui, on ne reviendrait aux cartes qu'en
          rechargeant l'application. */}
      <NavLink
        to={ACCUEIL}
        onClick={onNavigate}
        className="flex items-center gap-2 px-4 h-14 border-b border-slate-200 hover:bg-slate-100"
      >
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-slate-900 text-white text-sm font-bold">
          K
        </span>
        <span className="font-semibold tracking-tight">GERTRUDE</span>
      </NavLink>

      {/* Modules : à plat pour un collaborateur, en sections pour un profil
          qui porte plusieurs casquettes. Sept entrées à plat sont illisibles ;
          deux entrées en sections coûteraient un clic pour rien. */}
      <nav className="flex-1 overflow-y-auto py-3 space-y-0.5">
        {sections
          ? sections.map((s) => (
              <SectionMenu
                key={s.cle}
                section={s}
                activeKey={activeKey}
                compteurs={compteurs}
                onNavigate={onNavigate}
              />
            ))
          : modules.map((m) => (
              <ModuleLink
                key={m.key}
                to={m.to}
                icon={m.icon}
                label={m.label}
                active={activeKey === m.key}
                onClick={onNavigate}
              />
            ))}

        {/* Sans sections, l'administration garde son entrée séparée : c'est la
            navigation d'avant, et elle ne doit pas bouger. */}
        {!sections && isAdmin && (
          <>
            <div className="my-2 border-t border-slate-200 mx-3" />
            <ModuleLink
              to="/admin/utilisateurs"
              icon="👥"
              label="Utilisateurs"
              active={activeKey === 'admin'}
              onClick={onNavigate}
            />
          </>
        )}
      </nav>

      {/* Utilisateur + Sortir */}
      <div className="border-t border-slate-200 p-3">
        {user && (
          <div className="flex items-center gap-2 mb-2 px-1">
            {user.picture ? (
              <img src={user.picture} alt="" width={28} height={28} className="rounded-full" referrerPolicy="no-referrer" />
            ) : (
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-200 text-slate-600 text-xs font-medium">
                {(user.name ?? user.email).slice(0, 1).toUpperCase()}
              </span>
            )}
            <span className="text-sm text-slate-700 truncate" title={user.email}>
              {user.name ?? user.email}
            </span>
          </div>
        )}
        <button
          type="button"
          onClick={onLogout}
          className="w-full text-left px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100"
        >
          ↪ Sortir
        </button>
      </div>
    </div>
  );
}

function ModuleLink({
  to,
  icon,
  label,
  active,
  onClick,
  badge = 0,
}: {
  to: string;
  icon: string;
  label: string;
  active: boolean;
  onClick: () => void;
  /** Ce qui attend une action — affiché seulement s'il y a quelque chose. */
  badge?: number;
}) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={`flex items-center gap-2.5 mx-2 px-3 py-2 rounded-md text-sm transition-colors border-l-2 ${
        active
          ? 'bg-white text-slate-900 font-medium border-blue-600 shadow-sm'
          : 'text-slate-600 hover:bg-slate-100 border-transparent'
      }`}
    >
      <span className="text-base leading-none">{icon}</span>
      <span className="flex-1 leading-tight">{label}</span>
      {badge > 0 && (
        <span className="inline-flex min-w-5 justify-center rounded-full bg-amber-500 px-1.5 text-xs font-semibold text-white">
          {badge}
        </span>
      )}
    </NavLink>
  );
}

/**
 * Une section du menu, dépliable.
 *
 * Elle s'ouvre d'office quand on se trouve dedans : arriver sur un écran dont
 * l'entrée est cachée dans un tiroir fermé donne l'impression d'être perdu.
 *
 * Repliée, elle porte le NOMBRE DE VALIDATIONS EN ATTENTE — ce qui retient
 * quelqu'un d'autre. Sans ce report, replier une section reviendrait à masquer
 * le seul signal qui appelle une action.
 */
function SectionMenu({
  section,
  activeKey,
  compteurs,
  onNavigate,
}: {
  section: SectionDef;
  activeKey: string;
  compteurs: Compteurs | undefined;
  onNavigate: () => void;
}) {
  const contientLActif = section.modules.some((m) => m.key === activeKey);
  const [ouverte, setOuverte] = useState(contientLActif);
  const enAttente = aValiderDansSection(section.cle, compteurs);

  // Une section sans sous-menu est un simple lien : la replier n'aurait rien
  // à cacher.
  if (section.direct && section.modules.length === 1) {
    const m = section.modules[0]!;
    return (
      <ModuleLink
        to={m.to}
        icon={section.icone}
        label={section.libelle}
        active={activeKey === m.key}
        onClick={onNavigate}
        badge={enAttente}
      />
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setOuverte((o) => !o)}
        aria-expanded={ouverte}
        // min-h-11 = 44 px : la cible tactile minimale. Un en-tête de section
        // se tape au pouce sur le panneau coulissant du mobile.
        className={`w-full flex items-center gap-2 px-3 min-h-11 text-sm rounded-md mx-1 transition-colors ${
          contientLActif ? 'text-slate-900 font-medium' : 'text-slate-600 hover:bg-slate-100'
        }`}
      >
        <span aria-hidden>{section.icone}</span>
        <span className="flex-1 text-left">{section.libelle}</span>
        {/* Le badge ne s'affiche que replié : ouverte, l'information est déjà
            sous les yeux, module par module. */}
        {!ouverte && enAttente > 0 && (
          <span className="inline-flex min-w-5 justify-center rounded-full bg-amber-500 px-1.5 text-xs font-semibold text-white">
            {enAttente}
          </span>
        )}
        {/* Le chevron dit d'un coup d'œil si la section est ouverte : en
            `text-xs text-slate-400`, il fallait le chercher. Une seule flèche
            que l'on fait PIVOTER, plutôt que deux glyphes de tailles
            différentes : le mouvement se lit mieux que la forme. */}
        <svg
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden
          className={`h-5 w-5 shrink-0 text-slate-500 transition-transform duration-150 ${
            ouverte ? 'rotate-90' : ''
          }`}
        >
          <path d="M7.5 4.5 13 10l-5.5 5.5-1.4-1.4L10.2 10 6.1 5.9z" />
        </svg>
      </button>

      {ouverte && (
        <div className="ml-4 border-l border-slate-200 pl-1 space-y-0.5">
          {section.modules.map((m) => (
            <ModuleLink
              key={m.key}
              to={m.to}
              icon={m.icon}
              label={m.label}
              active={activeKey === m.key}
              onClick={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}
